import type { SecurityStep, EnhancedResource } from "../../types";
import { SECURITY_STEPS } from "../../constants";

// Calculate security step from unified state
export const calculateSecurityStepFromUnifiedState = (
  authStatus: "authenticated" | "pre-authenticated" | "unauthenticated",
  hasResources: boolean,
  hasAccess: boolean,
  debug?: (message: string, ...args: any[]) => void
): SecurityStep => {
  // Priority 1: Check auth status first
  if (authStatus === "unauthenticated") {
    return SECURITY_STEPS.AUTH_FAILED;
  }

  if (authStatus === "pre-authenticated") {
    return SECURITY_STEPS.PROFILE_NEEDED;
  }

  // Priority 2: If authenticated, check resource access
  if (authStatus === "authenticated") {
    // AIDEV-NOTE: Handle empty resources array (race condition fix)
    if (!hasResources) {
      // Start debug timer only once per empty session
      if (debug) {
        emptyResourcesTimer.start(() => {
          debug(
            "⚠️ Empty resources for 5s - might indicate race condition or config issue"
          );
        }, 5000);
      }
      return SECURITY_STEPS.CHECKING_RESOURCES;
    }

    // Clear timer when we get resources back
    emptyResourcesTimer.clear();

    return hasAccess ? SECURITY_STEPS.AUTHORIZED : SECURITY_STEPS.NO_ACCESS;
  }

  // Fallback
  return SECURITY_STEPS.CHECKING_AUTH;
};

// Simple debug timer utility for empty resources warning
const createDebugTimer = (() => {
  let timer: number | null = null;

  return {
    start: (callback: () => void, delayMs: number) => {
      if (!timer) {
        timer = window.setTimeout(() => {
          callback();
          timer = null;
        }, delayMs);
      }
    },
    clear: () => {
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    },
  };
})();

const emptyResourcesTimer = createDebugTimer;
